import { AppContextProps, emptyAppContext } from '../hooks/useApp'
import { callAxios } from '../hooks/useAxios'
import { SocketMessage } from '../types/messaging'
import { Device, DeviceHwID, ID, Port, Rule } from '../types/models'
import { MultioptRole, PortConfig, PortNumber, UncommittedConfig } from '../types/ports'
import { DeviceCollection, DeviceCollectionFilter, DeviceUpdate } from './DeviceCollection'
import { DeviceControl } from './DeviceControl'
import { getAppliance } from './appliances'
import { Appliance, ApplianceData, ApplianceSubtype, ApplianceType } from './appliances/Appliance'
import { getPortController } from './ports'
import { OptimizerPortControl } from './ports/OptimizerPortControl'
import { PortControl } from './ports/PortControl'

/**
 * A hook to be executed on a message.
 */
export type SystemMessageHook = (msg: SocketMessage) => void

/**
 * Class for maintaining global information of controllables.
 */
export class SystemControl {

  public collection: DeviceCollection
  public app: AppContextProps
  public initialized: boolean

  private messageCount: Record<DeviceHwID, Record<PortNumber, number>>
  private deviceControls: Record<ID, DeviceControl>
  private refreshFunction: () => void
  private refreshFilter: (s: SocketMessage) => boolean
  private hooks: SystemMessageHook[]

  constructor(app: AppContextProps, refresh: () => void) {
    this.messageCount = {}
    this.deviceControls = {}
    this.app = app
    this.refreshFunction = refresh
    this.refreshFilter = () => true
    this.collection = new DeviceCollection()
    this.hooks = []
    this.initialized = false
  }

  static setRefresh(refresh: () => void) {
    system.refreshFunction = refresh
  }

  static setRefreshFilter(filter: (s: SocketMessage) => boolean): (s: SocketMessage) => boolean {
    const old = system.refreshFilter
    system.refreshFilter = filter
    return old
  }

  refresh(): void {
    this.refreshFunction()
  }

  static canRefresh(s: SocketMessage): boolean {
    return system.refreshFilter(s)
  }

  /**
   * Register incoming message.
   */
  static addCount(hwId: DeviceHwID, portNumber: PortNumber): void {
    system.messageCount[hwId] = system.messageCount[hwId] || {}
    system.messageCount[hwId][portNumber] = system.messageCount[hwId][portNumber] || 0
    system.messageCount[hwId][portNumber]++
  }

  /**
   * Get the number of messages received.
   */
  static count(hwId: null | DeviceHwID, portNumber: null | PortNumber): number {
    if (hwId !== null && portNumber !== null) {
      if (hwId in system.messageCount) {
        return system.messageCount[hwId][portNumber] || 0
      }
    }
    return 0
  }

  static onMessage(msg: SocketMessage): void {
    system.collection.onMessage(msg)
    system.hooks.forEach(hook => hook(msg))
  }

  static setDevices(devices: Device[]): void {
    system.collection.setDevices(devices)
    system.deviceControls = {}
    for (const device of devices) {
      const ctrl = new DeviceControl(system, device)
      ctrl.setPortControls((device.ports || []).map(port => getPortController(ctrl, port)))
      system.deviceControls[device.id as ID] = ctrl
    }
  }

  static onUpdate(params: DeviceUpdate): void {
    system.collection.onUpdate(params)
  }

  static addRule(hwId: DeviceHwID, rule: Rule): void {
    system.collection.addRule(hwId, rule)
  }

  /**
   * Add a message hook that is called for each message received.
   */
  registerHook(hook: SystemMessageHook): void {
    this.hooks.push(hook)
  }

  /**
   * Delete a message hook that has been registered earlier.
   */
  unregisterHook(hook: SystemMessageHook): void {
    this.hooks = system.hooks.filter(h => hook !== h)
  }

  /**
   * Accessor for system control instance.
   */
  static control(app: AppContextProps, refresh: () => void): SystemControl {
    system.app = app
    system.refreshFunction = refresh

    if (!system.initialized) {
      SystemControl.setDevices(system.collection.devices)
      refresh()
      system.initialized = true
    }

    return system
  }

  /**
   * Filtering access.
   */
  ports<T extends PortControl<Port, PortConfig>>(filter: DeviceCollectionFilter): T[] {
    const { ports } = this.collection.filter(filter)
    return ports.filter(port => !!this.deviceControls[port.device_id as ID]).map(port => this.deviceControls[port.device_id as ID].portControlFor(port)) as T[]
  }

  devices(filter: DeviceCollectionFilter): Device[] {
    const { devices } = this.collection.filter(filter)
    return devices.filter(device => !!this.deviceControls[device.id as ID])
  }

  controls(filter: DeviceCollectionFilter): DeviceControl[] {
    const { devices } = this.collection.filter(filter)
    return devices.filter(device => !!this.deviceControls[device.id as ID]).map(device => this.deviceControls[device.id as ID])
  }

  /**
   * Find a device control for a device by its ID.
   */
  getDeviceControl(deviceId: ID): DeviceControl {
    const match = this.controls({ deviceId })
    if (!match.length) {
      throw new Error(`Cannot find device #${deviceId}.`)
    }
    return match[0]
  }

  /**
   * Find a device control for a device by its HwID.
   */
  getDeviceControlByHwId(hwId: DeviceHwID): DeviceControl {
    const match = this.controls({ deviceHwId: hwId })
    if (!match.length) {
      throw new Error(`Cannot find device HW ID ${hwId}.`)
    }
    return match[0]
  }

  /**
   * Check the configuration differencies for possible problems for any device.
   */
  hasProblems(): boolean {
    for (const deviceCtrl of this.controls('ALL')) {
      if (deviceCtrl.hasProblems()) {
        return true
      }
    }
    return false
  }

  /**
   * Other collection functions.
   */
  havePendingConfig() {
    return this.collection.havePendingConfig()
  }

  clearPendingConfig() {
    return this.collection.clearPendingConfig()
  }

  pendingConfig() {
    return this.collection.pendingConfig()
  }

  /**
   * Find the port control by port ID, or `undefined` when no loaded device has that port.
   *
   * Use this where the ID comes from outside the loaded devices — a URL parameter,
   * navigation state, a stored appliance — since the port may be gone, or there may be
   * no devices at all (the device list lives in local storage and is wiped together
   * with the session).
   */
  findPortControl(id: ID): PortControl<Port, PortConfig> | undefined {
    for (const deviceCtrl of Object.values(this.deviceControls)) {
      const ctrl = deviceCtrl.portControlForId(id)
      if (ctrl) {
        return ctrl
      }
    }
    return undefined
  }

  /**
   * Get the port control by port ID.
   *
   * Throws when not found: the callers pass IDs taken from the loaded devices
   * themselves, so a miss is a programming error. Use `findPortControl()` for IDs
   * that come from the outside.
   */
  portControlForId(id: ID): PortControl<Port, PortConfig> {
    const ctrl = this.findPortControl(id)
    if (!ctrl) {
      throw new Error(`Cannot find port controller for port ID ${id}.`)
    }
    return ctrl
  }

  /**
   * Gather suitable ports for the given appliance and subtype.
   */
  getSuitablePorts(appliance: Appliance, subtype: ApplianceSubtype): PortControl<Port, PortConfig>[] {
    const ret: PortControl<Port, PortConfig>[] = []
    for (const deviceControl of Object.values(this.deviceControls)) {
      for (const portControl of Object.values(deviceControl.portControlsById)) {
        if (appliance.isSuitable(portControl, subtype)) {
          ret.push(portControl)
        }
      }
    }

    return ret
  }

  /**
   * Resolve port ID → device ID by searching all device controls.
   */
  deviceIdForPort(portId: ID): ID {
    for (const [deviceId, deviceControl] of Object.entries(this.deviceControls)) {
      if (deviceControl.portControlForId(portId)) {
        return parseInt(deviceId) as ID
      }
    }
    throw new Error(`Cannot find device for port ID ${portId}.`)
  }

  /**
   * Flash explicit config items to devices.
   * Items are grouped by device ID and sent as a single PATCH per device.
   */
  async flashItems(items: UncommittedConfig[]): Promise<void> {
    // Group items by device ID
    const byDevice: Record<string, UncommittedConfig[]> = {}
    for (const item of items) {
      const deviceId = this.deviceIdForPort(item.id)
      const key = String(deviceId)
      byDevice[key] = byDevice[key] || []
      byDevice[key].push(item)
    }

    for (const [deviceId, flash] of Object.entries(byDevice)) {
      const res = await callAxios({
        method: 'PATCH',
        url: `/devices/config/${deviceId}`,
      }, { flash })
      if (!res.success) {
        this.app.addError(res)
      }
    }
  }

  /**
   * Check if any device is upgrading.
   */
  isUpgrading(): boolean {
    for (const deviceControl of Object.values(this.deviceControls)) {
      if (deviceControl.isUpgrading()) {
        return true
      }
    }

    return false
  }

  /**
   * Collect upgrade progress for devices upgrading.
   */
  upgradeProgress(): [DeviceHwID, number][] {
    const result: [DeviceHwID, number][] = []
    for (const deviceControl of Object.values(this.deviceControls)) {
      if (deviceControl.isUpgrading()) {
        result.push(deviceControl.upgradeProgress())
      }
    }

    return result
  }

  /**
   * Resolve multi optimization roles for every device.
   * @returns
   */
  multiOptRoles(): Record<ID, MultioptRole> {
    const ret: Record<ID, MultioptRole> = {}
    const locationDevices: Record<string, DeviceControl[]> = {}

    // Count meters per location.
    for (const deviceControl of Object.values(this.deviceControls)) {
      const locationId = deviceControl.device.location_id as ID
      locationDevices[locationId] = locationDevices[locationId] || []
      locationDevices[locationId].push(deviceControl)
    }

    // Resolve per device.
    Object.keys(locationDevices).forEach(locationId => {

      if (locationDevices[locationId].length <= 1) {
        ret[locationDevices[locationId][0].device.id as ID] = 'MULTIOPT_NO'
        return
      }

      // Configure no-opt devices
      locationDevices[locationId].filter(ctrl => ctrl.device.values?.multiopt_config_mode === 'MULTIOPT_CONFIG_NOOPT').forEach(ctrl => { ret[ctrl.device.id as ID] = 'MULTIOPT_NO' })

      // Filter out no-opt devices
      locationDevices[locationId] = locationDevices[locationId].filter(ctrl => ctrl.device.values?.multiopt_config_mode !== 'MULTIOPT_CONFIG_NOOPT')

      // Every device of the location opted out of multi-optimization. They are
      // all MULTIOPT_NO already and there is no leader to resolve. Without this
      // the automatic branch below would read locationDevices[locationId][0] of
      // an empty list and throw, aborting the whole save silently.
      if (locationDevices[locationId].length === 0) {
        return
      }

      // Automatic role selection runs when any device asks for it, and also when
      // no device is the explicit manual leader. Devices whose mode was never
      // saved (undefined) must fall in the automatic branch: treating them as
      // manual used to default every device to follower, leaving the whole
      // location without a leader.
      const modes = locationDevices[locationId].map(ctrl => ctrl.device.values?.multiopt_config_mode)
      const isAutomatic = modes.includes('MULTIOPT_CONFIG_AUTO') || !modes.includes('MULTIOPT_CONFIG_LEADER')

      if (isAutomatic) {

        // Automatic multi-optimization role assignment.
        //
        // The leader must be a device that has something of its own to control:
        // since fw42 a follower plans its battery only from the leader's
        // BATTERY_PLAN frame, and a fw43 leader without relays or a battery never
        // runs the planning cycle at all. So prefer a battery device, then one
        // with relay channels, then an energy meter, and only then the first one.
        const candidates = locationDevices[locationId]
        const firstDeviceWith = (type: Port['type']): ID | undefined => candidates
          .find(ctrl => ctrl.findPortsBy(p => p.port.type === type && SystemControl.assetEnabled(p)).length > 0)
          ?.device.id as ID | undefined

        const leader: ID = firstDeviceWith('ENERGY_STORAGE') ??
          firstDeviceWith('OPTIMIZER_CONTROL') ??
          firstDeviceWith('EM_3PH') ??
          firstDeviceWith('EM_HAN') ??
          candidates[0].device.id as ID

        for (let i = 0; i < locationDevices[locationId].length; i++) {
          if (locationDevices[locationId][i].device.id === leader) {
            ret[locationDevices[locationId][i].device.id as ID] = 'MULTIOPT_LEADER'
          } else {
            ret[locationDevices[locationId][i].device.id as ID] = 'MULTIOPT_FOLLOWER'
          }
        }
      } else {
        // Manual assignment of MULTIOPT_LEADER and MULTIOPT_FOLLOWER roles
        for (const ctrl of locationDevices[locationId]) {
          const multiOptConfigMode = ctrl.device.values?.multiopt_config_mode
          ret[ctrl.device.id as ID] = multiOptConfigMode === 'MULTIOPT_CONFIG_LEADER' ? 'MULTIOPT_LEADER' : 'MULTIOPT_FOLLOWER'
        }
      }
    })

    return ret
  }

  /**
   * Whether a port counts as an enabled asset for automatic leader election.
   *
   * A pending (about to be flashed) value wins. Otherwise trust what the device
   * itself reports (`config.hardware`) over the committed top-level value: the
   * top-level copy is refreshed only when a pending flash is confirmed, and when
   * the server gives up on a flash it drops the pending block without merging
   * the hardware read-back. That leaves e.g. a battery port at `enabled: false`
   * on the server while the device runs it enabled, and electing by the stale
   * copy made a meter-only device the leader ahead of the battery device.
   */
  static assetEnabled(portControl: PortControl<Port, PortConfig>): boolean {
    const config = portControl.port.config as unknown as Record<string, unknown>
    const pending = config.pending as Record<string, unknown> | undefined
    if (pending && pending.enabled !== undefined) {
      return !!pending.enabled
    }
    const hardware = config.hardware as Record<string, unknown> | null | undefined
    if (hardware && hardware.enabled !== undefined) {
      return !!hardware.enabled
    }
    return !!config.enabled
  }

  /**
   * Set up configuration for every optimizer multi opt roles.
   *
   * @return Flash items for ports whose multiopt_role changed.
   */
  configureMultiOptRoles(): UncommittedConfig[] {
    const items: UncommittedConfig[] = []
    const roles = this.multiOptRoles()
    for (const deviceId of Object.keys(roles)) {
      const ctrl = this.getDeviceControl(parseInt(deviceId) as ID)
      const port = ctrl.findPort('210/0' as PortNumber) as OptimizerPortControl
      if (port.getConfig('multiopt_role') !== roles[deviceId as unknown as ID]) {
        port.setConfig('multiopt_role', roles[deviceId as unknown as ID])
        items.push(DeviceControl.buildPortFlashItem(port))
      }
    }
    return items
  }

  /**
   * Find the suitable leader for a list of devices.
   * @param list
   */
  leader(list: DeviceControl[]) {
    if (list.length === 0) {
      throw new Error('Cannot select leader from the empty list.')
    }
    if (list.length === 1) {
      return list[0]
    }
    // By default use multi-opt leader or if not found, then just first.
    for (const deviceCtrl of list) {
      const optCtrl = deviceCtrl.findPortByType('OPTIMIZER') as OptimizerPortControl
      if (optCtrl.getConfig('multiopt_role') === 'MULTIOPT_LEADER') {
        return deviceCtrl
      }
    }
    return list[0]
  }

  /**
   * Calculate a list of devices that require also netsum appliance for their location.
   *
   * Return value is mapping from location IDs to the leader device.
   */
  netsumNeeded(): Record<ID, DeviceControl | null> {
    const locationDevices: Record<string, DeviceControl[]> = {}
    const result: Record<string, DeviceControl | null> = {}

    // Get devices per location.
    for (const deviceControl of Object.values(this.deviceControls)) {
      const locationId = deviceControl.device.location_id as ID
      locationDevices[locationId] = locationDevices[locationId] || []
      locationDevices[locationId].push(deviceControl)
    }

    Object.keys(locationDevices).forEach(id => {
      if (locationDevices[id].map(deviceCtrl => deviceCtrl.countSummers()).reduce((prev, cur) => prev + cur, 0) > 1) {
        result[id] = this.leader(locationDevices[id])
      } else {
        result[id] = null
      }
    })

    return result
  }

  /**
   * Convert a list of loaded appliance data to appliance instances.
   * @param appliances
   */
  makeAppliance(data: ApplianceData): Appliance {
    const app = getAppliance(data, this, this.app)
    app.connectControls(this)
    return app
  }

  /**
   * Gather appliances from one location.
   * @param locationId
   */
  async appliances(locationId: ID, type: ApplianceType | undefined = undefined, subtype: ApplianceSubtype | undefined = undefined): Promise<Appliance[]> {
    const res = await callAxios<ApplianceData[]>({
      method: 'GET',
      url: `/appliances?location=${locationId}`,
    })
    if (res.success) {
      const apps = (type === undefined ? res.data : res.data.filter(app => app.type === type)).map(a => this.makeAppliance(a))

      if (subtype) {
        return apps.filter(a => a.getSubtype() === subtype)
      }

      return apps
    }
    return []
  }
}

// TODO: Instead of dummy unusable initialized state, this should be static member which is null until functional.
const system = new SystemControl(emptyAppContext, () => null)
